/**
 * Pasticceria Elio — animazioni luxury (GSAP + ScrollTrigger + SplitType)
 * Rispetta prefers-reduced-motion; parallax/tilt disattivati sotto 768px.
 */
(() => {
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;

  /* ── Toni sezione → colore CSS (onde dinamiche) ── */
  const toneColor = (tone) => {
    const root = getComputedStyle(document.documentElement);
    const map = {
      white: root.getPropertyValue('--color-bg').trim() || '#ffffff',
      cream: root.getPropertyValue('--color-cream').trim() || '#f7f2ea',
      ivory: root.getPropertyValue('--color-ivory-warm').trim() || '#f7f2ea',
      stone: root.getPropertyValue('--color-stone').trim() || '#e5ddd0',
      hero: '#2f231c',
    };
    return map[tone] || map.white;
  };

  const getSectionTone = (sectionEl) => {
    if (!sectionEl) return 'white';
    return sectionEl.querySelector('[data-section-tone]')?.dataset.sectionTone || 'white';
  };

  const initWaveColors = () => {
    qsa('.section-wave-divider-section').forEach((wrapper) => {
      const wave = wrapper.querySelector('[data-wave-divider]');
      if (!wave || wave.dataset.waveAuto !== 'true') return;

      const prevTone = getSectionTone(wrapper.previousElementSibling);
      const nextTone = getSectionTone(wrapper.nextElementSibling);

      wrapper.style.backgroundColor = toneColor(prevTone);
      wave.style.setProperty('--wave-fill', toneColor(nextTone));
    });
  };

  initWaveColors();

  const showAll = () => {
    document.documentElement.classList.add('motion-reduced');
    qsa('[data-reveal], .reveal').forEach((el) => {
      el.classList.add('is-visible');
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    qsa('[data-reveal-stagger] .review-card').forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    qsa('[data-ingredient-media]').forEach((el) => el.classList.add('is-revealed'));
    qsa('[data-wave-divider]').forEach((el) => {
      el.classList.add('is-drawn');
      initWaveColors();
    });
    qsa('.reveal-text').forEach((el) => el.classList.add('is-split'));
  };

  if (prefersReduced || typeof gsap === 'undefined') {
    showAll();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ── 1. Reveal a scroll (batch per performance) ── */
  const revealTargets = qsa('[data-reveal], .reveal');
  revealTargets.forEach((el) => el.setAttribute('data-luxury-reveal', ''));

  if (revealTargets.length) {
    ScrollTrigger.batch(revealTargets, {
      start: 'top 88%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          stagger: 0.08,
          ease: 'power2.out',
          overwrite: true,
        });
      },
    });

    gsap.set(revealTargets, { autoAlpha: 0, y: 28 });
  }

  /* ── 2. Testo parola per parola (.reveal-text) ── */
  const splitTitles = qsa('.reveal-text');
  if (splitTitles.length && typeof SplitType !== 'undefined') {
    splitTitles.forEach((el) => {
      const split = new SplitType(el, { types: 'words' });
      el.classList.add('is-split');
      const words = split.words || [];
      words.forEach((word) => {
        const wrap = document.createElement('span');
        wrap.className = 'word';
        const inner = document.createElement('span');
        inner.className = 'word-inner';
        inner.innerHTML = word.innerHTML;
        word.innerHTML = '';
        wrap.appendChild(inner);
        word.appendChild(wrap);
      });

      const isHero = el.closest('.hero');
      gsap.set(el.querySelectorAll('.word-inner'), { y: '110%', autoAlpha: 0 });
      if (isHero) {
        gsap.to(el.querySelectorAll('.word-inner'), {
          y: 0,
          autoAlpha: 1,
          duration: 0.9,
          stagger: 0.06,
          ease: 'power3.out',
          delay: 0.25,
        });
      } else {
        ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => {
            gsap.to(el.querySelectorAll('.word-inner'), {
              y: 0,
              autoAlpha: 1,
              duration: 0.75,
              stagger: 0.05,
              ease: 'power2.out',
            });
          },
        });
      }
    });
  }

  /* ── 3. Hero — parallax differenziale ── */
  const hero = qs('.hero--luxury');
  const heroMedia = qs('[data-hero-parallax]', hero);
  if (hero && heroMedia && isDesktop()) {
    gsap.to(heroMedia, {
      yPercent: 18,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }

  /* ── 4. Bottoni magnetici ── */
  if (isDesktop()) {
    qsa('[data-magnetic]').forEach((btn) => {
      const radius = 15;
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const clampedX = Math.max(-radius, Math.min(radius, x * 0.2));
        const clampedY = Math.max(-radius, Math.min(radius, y * 0.2));
        gsap.to(btn, { x: clampedX, y: clampedY, duration: 0.35, ease: 'power2.out' });
      });
      btn.addEventListener('mouseleave', () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.6)' });
      });
    });
  }

  /* ── 5. Divisori SVG onda — draw stroke ── */
  qsa('[data-wave-divider]').forEach((wrap) => {
    const stroke = wrap.querySelector('.section-wave-divider__stroke');
    if (!stroke || typeof stroke.getTotalLength !== 'function') {
      wrap.classList.add('is-drawn');
      return;
    }
    const len = stroke.getTotalLength();
    gsap.set(stroke, { strokeDasharray: len, strokeDashoffset: len });
    ScrollTrigger.create({
      trigger: wrap,
      start: 'top 92%',
      once: true,
      onEnter: () => {
        gsap.to(stroke, {
          strokeDashoffset: 0,
          duration: 1.4,
          ease: 'power2.inOut',
          onComplete: () => wrap.classList.add('is-drawn'),
        });
      },
    });
  });

  /* ── 6. Icone ingredienti — draw-in ── */
  qsa('[data-svg-draw]').forEach((wrap) => {
    const paths = wrap.querySelectorAll('.svg-draw-icon__path');
    paths.forEach((path) => {
      if (typeof path.getTotalLength !== 'function') return;
      const len = path.getTotalLength();
      gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
    });
    ScrollTrigger.create({
      trigger: wrap,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(paths, {
          strokeDashoffset: 0,
          duration: 1.2,
          stagger: 0.12,
          ease: 'power2.out',
        });
      },
    });
  });

  /* ── 7. Sottolineatura titoli ingredienti ── */
  qsa('[data-title-line]').forEach((line) => {
    gsap.set(line, { width: 0 });
    ScrollTrigger.create({
      trigger: line.closest('.ingredient-block') || line,
      start: 'top 82%',
      once: true,
      onEnter: () => {
        gsap.to(line, { width: '100%', duration: 0.75, ease: 'power2.out' });
      },
    });
  });

  /* ── 8. Immagini ingredienti — scale + fade ── */
  qsa('[data-ingredient-media]').forEach((media) => {
    const img = media.querySelector('img');
    if (!img) {
      media.classList.add('is-revealed');
      return;
    }
    gsap.set(img, { scale: 1.05, autoAlpha: 0 });
    ScrollTrigger.create({
      trigger: media,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        gsap.to(img, {
          scale: 1,
          autoAlpha: 1,
          duration: 1,
          ease: 'power2.out',
          onComplete: () => media.classList.add('is-revealed'),
        });
      },
    });
  });

  /* ── 9. Bordo animato card prodotto (stroke-dash) ── */
  qsa('.product-card__border-draw rect, .product-spotlight__border-draw rect').forEach((rect) => {
    if (typeof rect.getTotalLength === 'function') {
      const len = rect.getTotalLength();
      rect.style.strokeDasharray = String(len);
      rect.style.strokeDashoffset = String(len);
    }
  });

  /* ── 10. Tilt 3D gift cards ── */
  if (isDesktop()) {
    qsa('[data-tilt-card]').forEach((card) => {
      const max = 4;
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        gsap.to(card, {
          rotateY: px * max * 2,
          rotateX: -py * max * 2,
          transformPerspective: 900,
          duration: 0.35,
          ease: 'power2.out',
        });
      });
      card.addEventListener('mouseleave', () => {
        gsap.to(card, {
          rotateX: 0,
          rotateY: 0,
          duration: 0.55,
          ease: 'power2.out',
        });
      });
    });
  }

  /* ── 11. Recensioni — stagger ingresso ── */
  qsa('[data-reveal-stagger]').forEach((grid) => {
    const cards = [...grid.querySelectorAll('.review-card')];
    if (!cards.length) return;
    gsap.set(cards, { autoAlpha: 0, y: 24 });
    ScrollTrigger.create({
      trigger: grid,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(cards, {
          autoAlpha: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.15,
          ease: 'power2.out',
        });
      },
    });
  });

  window.addEventListener('load', () => ScrollTrigger.refresh());
})();
